'use client';

/**
 * Client-side helpers that hit the consumer-app BFF (`/api/consumer/notifications/*`)
 * which proxies to the docuproconsole backend.
 */

export type NotificationItem = {
  id: number;
  title: string | null;
  body: string;
  deepLink?: string | null;
  category: 'TRANSACTIONAL' | 'PROMOTIONAL' | 'ANNOUNCEMENT';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  eventKey?: string | null;
  data?: Record<string, unknown> | null;
  readAt?: string | null;
  createdAt: string;
};

export type NotificationPage = {
  items: NotificationItem[];
  nextCursor?: string | null;
  unreadCount: number;
};

export type NotificationPreferences = {
  pushOptIn: boolean;
  promotionalOptIn: boolean;
  announcementOptIn: boolean;
  quietHoursStart?: number | null;
  quietHoursEnd?: number | null;
  timezone?: string | null;
};

export type RegisterDeviceInput = {
  token: string;
  platform: 'WEB' | 'IOS' | 'ANDROID';
  appVersion?: string;
  locale?: string;
  timezone?: string;
  installationId?: string;
  lat?: number;
  lng?: number;
  cityName?: string;
};

export type AnonymousDeviceInput = RegisterDeviceInput & { installationId: string };

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Request failed (${res.status}): ${txt}`);
  }
  return (await res.json()) as T;
}

export async function registerDevice(input: RegisterDeviceInput) {
  return jsonOrThrow<{ success: boolean; deviceId?: number }>(
    await fetch('/api/consumer/notifications/devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
}

/** Register a NOT-logged-in device (by installationId) for push + location. */
export async function registerAnonymousDevice(input: AnonymousDeviceInput) {
  return jsonOrThrow<{ success: boolean; deviceId?: number }>(
    await fetch('/api/consumer/notifications/devices/public', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
}

/** Cheap foreground ping to refresh last-active (+ optional location) for an install. */
export async function heartbeatDevice(input: {
  installationId: string;
  lat?: number;
  lng?: number;
  cityName?: string;
}) {
  return jsonOrThrow<{ success: boolean; updated?: number }>(
    await fetch('/api/consumer/notifications/devices/heartbeat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
}

export async function revokeDevice(token: string) {
  return jsonOrThrow<{ success: boolean }>(
    await fetch('/api/consumer/notifications/devices', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  );
}

export async function fetchNotifications(params: { cursor?: string; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  return jsonOrThrow<{ success: boolean; data: NotificationPage }>(
    await fetch(`/api/consumer/notifications${q.toString() ? `?${q}` : ''}`)
  );
}

export async function fetchUnreadCount() {
  return jsonOrThrow<{ success: boolean; data: { count: number } }>(
    await fetch('/api/consumer/notifications/unread-count')
  );
}

export async function markRead(input: { ids?: number[]; all?: boolean }) {
  return jsonOrThrow<{ success: boolean; updated: number }>(
    await fetch('/api/consumer/notifications/mark-read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
}

export async function dismissNotifications(input: { ids?: number[]; all?: boolean }) {
  return jsonOrThrow<{ success: boolean; data: { dismissed: number } }>(
    await fetch('/api/consumer/notifications', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
  );
}

export async function fetchPreferences() {
  return jsonOrThrow<{ success: boolean; data: NotificationPreferences }>(
    await fetch('/api/consumer/notifications/preferences')
  );
}

export async function updatePreferences(prefs: Partial<NotificationPreferences>) {
  return jsonOrThrow<{ success: boolean; data: NotificationPreferences }>(
    await fetch('/api/consumer/notifications/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
    })
  );
}
